import React, { useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import VideoModal from 'src/components/VideoModal';
import logo from 'src/images/logo-md.png';
import { useOnMount } from 'src/lib/hooks';
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Formik,
  FormInput,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Link,
  Row,
  Stripes,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  toast,
  useApp
} from 'straightline-ui';
import { formatMoney } from 'straightline-utils/accounting';
import { truckType } from 'straightline-utils/constants';
import { formatDate } from 'straightline-utils/time';
import { yup } from 'straightline-utils/validation';

const schema = yup.object({
  strategy: yup.string().oneOf(['credential']).required(),
  email: yup.string().trim().email().label('Email').required(),
  password: yup.string().trim().required().label('Password')
});

export default function Login({ location, history }) {
  const app = useApp();
  const [showForm, setShowForm] = useState(false);

  useOnMount(() => {
    const handleAutoLogin = async () => {
      try {
        await app.authenticate(location.query.token);
        if (location.query && location.query.redirect) {
          history.push(location.query.redirect);
        } else {
          history.push('/');
        }
      } catch (error) {
        toast.errors(error.message);
      }
    };

    if (location.query?.token) {
      handleAutoLogin();
    }
  });

  const { result } = app.service('api/signup').useFind();

  return (
    <Container fluid>
      <Row className="justify-content-center">
        <Col
          xs="12"
          md="6"
          lg="5"
          xl="4"
          className="px-lg-5 border-lg-right d-flex align-items-center min-h-100vh"
        >
          <div className="w-100">
            <div className="text-center my-4">
              <img
                src={logo}
                alt="Straightline Logo"
                className="img-fluid"
                style={{ height: 60 }}
              />
            </div>
            <div className="mb-3">
              Straightline uses Highway to onboard and verify carriers. Click
              the button below to sign in. You can then return to Straightline
              to access the dashboard and book loads.
            </div>
            <div className="mb-4">
              If you are having trouble signing in, please contact{' '}
              <a href="mailto:support@straightline.com">
                {process.env.REACT_APP_SUPPORT_EMAIL}
              </a>
            </div>
            {showForm && <LoginForm />}
            <Button
              tag="a"
              href={process.env.REACT_APP_HIGHWAY_AUTH_URL}
              color="warning"
              size="lg"
              block
              style={{
                color: 'white',
                backgroundColor: '#F8C617',
                borderColor: '#F8C617'
              }}
            >
              <svg
                className="mr-2"
                width="22"
                height="22"
                viewBox="0 0 35 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22.7139 21.802H34.4453L25.7554 0.199219H19.8897L20.8673 7.90674H14.024L15.1103 0.199219H9.24458L0.554688 21.802H12.286L13.2636 14.0945H21.7363L22.7139 21.802Z"
                  fill="white"
                />
              </svg>

              <span>Sign in with Highway</span>
            </Button>
            {!showForm && (
              <Button
                size="lg"
                color="primary"
                block
                onClick={() => setShowForm(true)}
              >
                Sign in with Email
              </Button>
            )}
            <div className="text-muted small mt-2">
              Don't have an account?&nbsp;
              <Link to="/signup">Sign up</Link>.
            </div>
          </div>
        </Col>
        <Col lg="7" xl="8" className="d-none d-lg-block px-0">
          <div
            className="d-flex align-items-center py-4"
            style={{
              overflow: 'hidden',
              position: 'relative',
              minHeight: '100vh'
            }}
          >
            <Stripes animation="fadeIn" />
            <Container fluid className="px-4">
              <Row className="d-flex justify-content-center">
                <Col xl="9">
                  <div className="text-white">
                    <h1 style={{ fontSize: '3.5rem' }} className="mb-4">
                      Freight to the Point
                    </h1>
                    <h3 style={{ fontSize: '1.5rem' }} className="mb-4">
                      Let the loads find you, right now.
                    </h3>
                    <p className="lead mb-4">
                      Get competitive rates and book a load in seconds without
                      leaving the app. After signing in, you can immediately
                      access the Straightline Dashboard and begin booking loads.
                      Straightline has no subscription fees or hidden costs.
                    </p>
                  </div>
                  <Card style={{ height: 442 }}>
                    <div className="table-responsive">
                      <Table className="table table-nowrap card-table table-hover">
                        <TableHead>
                          <TableRow>
                            <TableHeader className="px-3 px-xl-4">
                              Payout
                            </TableHeader>
                            <TableHeader className="px-3 px-xl-4">
                              Pickup
                            </TableHeader>
                            <TableHeader className="px-3 px-xl-4">
                              Delivery
                            </TableHeader>
                            <TableHeader className="px-3 px-xl-4">
                              Service
                            </TableHeader>
                          </TableRow>
                        </TableHead>
                        <TableBody className="list">
                          {result?.data?.map((match) => {
                            return (
                              <TableRow
                                key={match.id}
                                onClick={() => window.open(match.url, '_blank')}
                                className="pointer"
                              >
                                <TableData
                                  className="px-3 px-xl-4"
                                  style={{ width: '1%' }}
                                >
                                  <div className="h1 mb-0 text-primary">
                                    {formatMoney(
                                      match.rate.price.carrier_price,
                                      {
                                        precision: 0
                                      }
                                    )}
                                  </div>
                                </TableData>
                                <TableData
                                  className="px-3 px-xl-4"
                                  style={{ width: '1%' }}
                                >
                                  <p className="mb-0">
                                    {match.pickup_address.city},{' '}
                                    {match.pickup_address.state}
                                  </p>
                                  <p className="mb-0">
                                    {formatDate(
                                      match.pickup_date,
                                      'LLL d, yyyy'
                                    )}
                                  </p>
                                </TableData>
                                <TableData
                                  className="px-3 px-xl-4"
                                  style={{ position: 'relative', width: '1%' }}
                                >
                                  <div
                                    style={{
                                      position: 'absolute',
                                      left: -12,
                                      top: 21
                                    }}
                                  >
                                    <Icon
                                      type="arrow-right text-success"
                                      style={{ fontSize: 25 }}
                                    />
                                  </div>
                                  <p className="mb-0">
                                    {match.delivery_address.city},{' '}
                                    {match.delivery_address.state}
                                  </p>
                                  <p className="mb-0">
                                    {formatDate(match.due_date, 'LLL d, yyyy')}
                                  </p>
                                </TableData>
                                <TableData>
                                  <div>{match.rate.service_level.name}</div>
                                  <div className="d-flex align-items-center">
                                    <Icon
                                      type="truck"
                                      className="text-secondary"
                                    />
                                    <span className="ml-1">
                                      {truckType(match.truck_type).label}
                                    </span>
                                  </div>
                                </TableData>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </Card>
                  <div>
                    <VideoModal className="mr-3" />
                    <a
                      href="https://www.bookstraightline.com/carriers"
                      className="btn btn-white"
                    >
                      Learn More
                    </a>
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

function LoginForm() {
  const app = useApp();
  const [showPassword, setShowPassword] = useState(false);
  const passwordRef = useRef(null);
  const history = useHistory();
  const location = useLocation();

  const handleSubmit = (data, formik) => {
    formik.setSubmitting(true);
    app
      .authenticate(data)
      .then(() => {
        formik.setSubmitting(false);
        if (location.query && location.query.redirect) {
          history.push(location.query.redirect);
        } else {
          history.push('/');
        }
      })
      .catch((error) => {
        formik.setSubmitting(false);
        formik.setFieldValue('password', '');
        formik.setFieldTouched('password', false);
        passwordRef.current?.focus();
        if (error.formik) {
          formik.setErrors(error.formik);
        } else {
          toast.error(error.message);
        }
      });
  };

  return (
    <div>
      <Formik
        onSubmit={handleSubmit}
        initialValues={{
          strategy: 'credential',
          email: '',
          password: ''
        }}
        validationSchema={schema}
        render={({ values, isSubmitting }) => {
          return (
            <React.Fragment>
              <Form>
                <FormGroup>
                  <Label>Email*</Label>
                  <FormInput
                    type="email"
                    name="email"
                    placeholder="name@address.com"
                    innerRef={passwordRef}
                    disabled={isSubmitting}
                  />
                  <FormFeedback name="email" />
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col>
                      <Label>Password*</Label>
                    </Col>
                    <Col xs="auto">
                      <Link
                        tabIndex={-1}
                        to={
                          values.email
                            ? `/forgot-password?email=${encodeURIComponent(
                                values.email
                              )}`
                            : '/forgot-password'
                        }
                        className="form-text text-secondary"
                      >
                        Forgot password?
                      </Link>
                    </Col>
                  </Row>
                  <InputGroup className="input-group-merge">
                    <FormInput
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      className="form-control-appended"
                      placeholder="Enter your password"
                      disabled={isSubmitting}
                      innerRef={passwordRef}
                    />
                    <InputGroupAddon addonType="append">
                      <InputGroupText>
                        <Icon
                          type={showPassword ? 'eye-off' : 'eye'}
                          className="pointer"
                          onClick={() => {
                            setShowPassword(!showPassword);
                          }}
                        />
                      </InputGroupText>
                    </InputGroupAddon>
                    <FormFeedback name="password" />
                  </InputGroup>
                </FormGroup>
                <Button
                  type="submit"
                  color="primary"
                  size="lg"
                  block
                  className={`mb-2 ${isSubmitting && 'is-loading'}`}
                  disabled={isSubmitting}
                >
                  Sign in
                </Button>
              </Form>
            </React.Fragment>
          );
        }}
      />
    </div>
  );
}
